import React, {useState} from "react";
import {useDropzone} from "react-dropzone";
import {LoadingIndicator} from "./LoadingIndicator";
import {PreviewStage} from "./PreviewStage";
import Textures from "oc-threejs/Textures";
import {MaterialJson} from "../../nodes/metadata/MaterialJson";
import Scenes from "oc-threejs/Scenes";
import Commons from "oc-commons/Commons";
import {AbstractUploadProperty} from "../../nodes/properties/AbstractUploadProperty";

export function MaterialImport({setSelected, nodeIndex}) {

    const [changeCounter, setChangeCounter] = useState(Math.random());
    const [previewModel, setPreviewModel] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);
    const [uploading, setUploading] = useState(false);

    const materials = previewModel && Textures.getMaterials(previewModel);
    const sceneNode = APP.sceneNode;
    const materialPropertyList = sceneNode.getMaterialJsonProperty();

    const replaceKnownMaterials = (object3d) => {
        const swap = (material3d) => {
            const node = materialPropertyList.findKey(material3d.name);
            if (node) {
                if (!node.updateSceneCalled) {
                    node.updateScene();
                }
                return node.threeValue;
            }
            return material3d;
        };
        Scenes.filter(object3d, (o) => o.material != null).forEach((o) => {
            if (Array.isArray(o.material)) {
                o.material = o.material.map(swap);
            } else {
                o.material = swap(o.material);
            }
        });
    }

    const {
        acceptedFiles,
        fileRejections,
        getRootProps,
        getInputProps
    } = useDropzone({
        multiple: false,
        maxFiles: 1,
        onDrop: (acceptedFiles => {
            let file = acceptedFiles[0];
            if (file) {
                setUploading(true);
                setErrorMessage(null);
                const loader = new THREE.GLTFLoader();
                const url = URL.createObjectURL(file);
                loader.load(url, (gltf) => {
                    setUploading(false);
                    gltf.scene.sourceFile = file.name;
                    replaceKnownMaterials(gltf.scene);
                    setChangeCounter(changeCounter + 1);
                    setPreviewModel(gltf.scene);
                }, undefined, errorObject => {
                    setUploading(false);
                    setErrorMessage(errorObject);
                });
            }
        })
    });

    const dropZone = (content) => (
        <div className="container" title="upload">
            <div {...getRootProps({className: 'dropzone'})}>
                <input {...getInputProps()} />
                {content()}
            </div>
            {fileRejections.map(({file, errors}) => (
                <li key={file.name}>
                    {file.name} - {file.size} bytes
                    <ul>
                        {errors.map(e => (
                            <li key={e.code}>{e.message}</li>
                        ))}
                    </ul>
                </li>
            ))}
        </div>
    );

    return (
        <div className="flex-column">
            {previewModel == null && (
                <p className="info">
                    Für den Materialimport wird ein 3d-Modell im .glb - Format benötigt.
                </p>
            )}
            <div className="upload-input flex-row">
                {previewModel != null
                    ? (
                        <React.Fragment>
                            <PreviewStage
                                appScene={APP.scene}
                                changeDependencies={[changeCounter, nodeIndex]}
                                title={previewModel.sourceFile}
                                className="model-preview"
                                initStage={(stage) => {
                                    stage.scene.add(previewModel);
                                    stage.zoomTo(previewModel);
                                    stage.autoRotate();
                                }}
                            >
                                {uploading && (
                                    <LoadingIndicator className={'dark-background'}/>
                                )}
                            </PreviewStage>
                            <div className="flex-column">
                                <div className="delete button rounded"
                                     onClick={() => setPreviewModel(null)} title="Löschen">✖
                                </div>
                                {dropZone(() => (
                                    <div className="button upload rounded">▲</div>
                                ))}
                            </div>
                        </React.Fragment>
                    )
                    : (uploading
                            ? (
                                <React.Fragment>
                                    <LoadingIndicator/>
                                    <div className="delete button rounded"
                                         onClick={() => setUploading(false)} title="Abbrechen">✖
                                    </div>
                                </React.Fragment>
                            )
                            : (
                                dropZone(() => (
                                    <div className="button upload">upload ▲</div>
                                ))
                            )
                    )
                }
            </div>
            <br/>
            <div className="flex-column">
                {materials?.length > 0 && (
                    <div className="materials">
                        <p className="info">
                            Die im Modell enthaltenen Materialien können einzeln ausgewählt
                            und importiert werden. Importierte Materialien sind in der
                            Material-Liste
                            aufgeführt. Beim Laden aller 3D-Objekte werden deren Materialien
                            durch Materialien der Material-Liste ersetzt, falls der Material-Name
                            übereinstimmt.
                        </p>
                        <h3>Materialien:</h3>
                        <ul>
                            {materials
                                .map(material3d => {
                                    const foundMaterialNode = materialPropertyList.findKey(material3d.name);
                                    return (
                                        <li key={material3d.name}>
                                            {foundMaterialNode ? (
                                                <React.Fragment>
                                                    <div className="button"
                                                         onClick={() => {
                                                             setSelected(foundMaterialNode, materialPropertyList);
                                                         }}>
                                                        {material3d.name}
                                                    </div>
                                                    &nbsp;
                                                    <span>(bereits importiert)</span>
                                                </React.Fragment>
                                            ) : (
                                                <React.Fragment>
                                                    <span>{material3d.name}</span>
                                                    &nbsp;
                                                    <SingleMaterialImport
                                                        onClick={async (setUploading) => {
                                                            const checkAllTextureParametersAreSame = (material3d) => {
                                                                const textureKeys = Object.keys(material3d).filter(key => material3d[key] instanceof THREE.Texture);
                                                                if (textureKeys.length > 0) {
                                                                    const firstTexture = material3d[textureKeys[0]];
                                                                    const texturLog = [];
                                                                    textureKeys.forEach(key => {
                                                                        const texture = material3d[key];
                                                                        ['FlipY', 'WrapS', 'WrapT', 'Rotation', 'Repeat', 'Center', 'Offset'].forEach(p => {
                                                                            if (!Commons.isEqualEpsilon(firstTexture[p], texture[p])) {
                                                                                texturLog.push({
                                                                                    key,
                                                                                    p,
                                                                                    a: firstTexture[p],
                                                                                    b: texture[p]
                                                                                });
                                                                            }
                                                                        });
                                                                    });
                                                                    if (texturLog.length > 0) {
                                                                        console.warn('Abweichung der Textureparameter');
                                                                        console.table(texturLog);
                                                                        setErrorMessage('abweichende Texturparameter ' + texturLog.map(l => l.key + l.p).join(','));
                                                                    }
                                                                }
                                                            };
                                                            checkAllTextureParametersAreSame(material3d);

                                                            setUploading(true);
                                                            const metaTable = materialPropertyList.metaTable;
                                                            const materialNodeToImport = foundMaterialNode ??
                                                                new MaterialJson(metaTable, {id: undefined});
                                                            materialNodeToImport.name = material3d.name;
                                                            materialNodeToImport.fromThree(material3d);
                                                            const uploadProps = materialNodeToImport.getProperties().filter(p => p instanceof AbstractUploadProperty);
                                                            const everythingUploaded = () => uploadProps.every(p => !(p as any).uploadPending);
                                                            await Commons.sleepUntil(everythingUploaded);
                                                            materialNodeToImport.updateMeta();
                                                            if (foundMaterialNode) {
                                                                foundMaterialNode.removeOnServer();
                                                                materialPropertyList.remove(foundMaterialNode);
                                                            }
                                                            materialNodeToImport.saveToServer();
                                                            materialPropertyList.add(materialNodeToImport);
                                                            materialNodeToImport.updateScene();
                                                            // const json2 = materialNodeToImport.toJSON();
                                                            // Debugs.logDiffMaterial(freshMaterialNode.material3d);
                                                            replaceKnownMaterials(previewModel);
                                                            setChangeCounter(changeCounter + 1);
                                                            setUploading(false);
                                                        }}
                                                    />

                                                </React.Fragment>
                                            )}
                                        </li>
                                    );
                                })}
                        </ul>
                    </div>
                )}
            </div>
            {errorMessage != null && (
                <div className="error" dangerouslySetInnerHTML={{__html: errorMessage}}></div>
            )}
        </div>
    );
}

export function SingleMaterialImport({onClick}) {
    const [uploading, setUploading] = useState(false);
    return uploading ? (
        <LoadingIndicator className="inline-block"/>
    ) : (
        <div className="button" onClick={() => onClick(setUploading)}>
            importieren
        </div>
    );
}
